import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Application as PixiApplication, Graphics as PixiGraphics } from 'pixi.js';
import { useUi } from '@platform/ui/config';
export function WinEffects({ eventId, jackpot=false }: { eventId: string; jackpot?:boolean }) {
  const { motion } = useUi();
  const element = useRef<HTMLDivElement>(null);
  const engine = useRef<{app:PixiApplication;Graphics:typeof PixiGraphics}|null>(null);
  const previous = useRef(eventId);
  const [ready,setReady] = useState(false);
  // Prepare an idle renderer before an award. Loading Pixi/GPU at the moment
  // the popup opens would make the first celebration visibly late.
  useEffect(() => {
    const target=element.current;
    if (!target || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let disposed=false;
    void import('pixi.js').then(async ({Application,Graphics})=>{
      const app=new Application();
      await app.init({resizeTo:window,backgroundAlpha:0,antialias:true,preference:'webgl',autoStart:false});
      if(disposed){app.destroy(true,{children:true});return;}
      target.appendChild(app.canvas);engine.current={app,Graphics};setReady(true);
    }).catch(()=>{ /* Results stay usable without GPU rendering. */ });
    return ()=>{disposed=true;engine.current?.app.destroy(true,{children:true});engine.current=null;};
  },[]);
  useLayoutEffect(()=>{
    const renderer=engine.current,target=element.current;
    if(!ready||!renderer||!target)return;
    const {app,Graphics}=renderer;
    app.ticker.stop();
    app.stage.removeChildren().forEach(child=>child.destroy());
    if(!eventId||previous.current===eventId||matchMedia('(prefers-reduced-motion: reduce)').matches){app.render();return;}
    previous.current=eventId;
    app.renderer.resize(target.clientWidth||innerWidth,target.clientHeight||innerHeight);
    const colors=jackpot?[getComputedStyle(target).getPropertyValue('--room-accent').trim()||'#ffd84f','#fff1b5','#ffffff']:motion.confettiColors;
    const dots=Array.from({length:motion.confettiCount*(jackpot?2:1)},(_,i)=>{
      const angle=i*2.399,g=new Graphics().rect(-2,-5,4,10).fill(colors[i%colors.length]);
      g.x=app.screen.width/2+Math.cos(angle)*(12+i%24);g.y=app.screen.height*.3+Math.sin(angle)*(12+i%24);app.stage.addChild(g);
      return {g,vx:Math.cos(angle)*(2+i%7)*(jackpot?1.3:1),vy:-3-i%9};
    });
    let time=0;
    const tick=(t:{deltaTime:number})=>{
      time+=t.deltaTime;
      for(const p of dots){p.vy+=.14*t.deltaTime;p.g.x+=p.vx*t.deltaTime;p.g.y+=p.vy*t.deltaTime;p.g.rotation+=.03;p.g.alpha=Math.max(0,1-time/motion.confettiLifetimeFrames);}
      if(time>motion.confettiLifetimeFrames)app.ticker.stop();
    };
    // Draw before the popup's first browser paint, then continue the burst.
    app.render();app.ticker.add(tick);app.ticker.start();
    return ()=>{app.ticker.remove(tick);app.ticker.stop();};
  },[eventId,ready,motion,jackpot]);
  return <div ref={element} className="win-effects" aria-hidden="true" />;
}
